import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../../config";
import RoleDashboard from "./RoleDashboard";
import MySportsWidget from "./MySportsWidget";
import LabRequirementsWidget from "./LabRequirementsWidget";
import s from "./ClassList.module.css";
import m from "./StudentDashboard.module.css";

/**
 * Fetches everything the dashboard needs to render the student's schedule and,
 * when it's empty, a diagnosis explaining why. Notably the "effective section"
 * is derived from the live enrollments list — NOT from the session's cached
 * `linkedEntity.currentSectionId`. That cache is populated at login time and
 * goes stale the moment an admin edits/deletes the student's enrollment, so
 * trusting it produces phantom sections (e.g. dashboard still shows "Grade 10
 * · St. Catherine" after every enrollment is removed).
 */
function useStudentSchedule(studentId) {
  const [state, setState] = useState({
    loading: true,
    error: "",
    classes: [],
    lookups: {},
    activeSemester: null,
    sectionInfo: null,
    effectiveSectionId: null,
    isEnrolledForActive: false,
    latestEnrollment: null,
    hasAnyEnrollment: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [semesters, schoolYears, sections, gradeLevels, enrollments] = await Promise.all([
          fetch(`${API_BASE}/api/semesters`).then(r => r.json()),
          fetch(`${API_BASE}/api/school-years`).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE}/api/sections`).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE}/api/grade-levels`).then(r => r.json()).catch(() => []),
          studentId
            ? fetch(`${API_BASE}/api/enrollments?studentId=${studentId}`).then(r => r.json()).catch(() => [])
            : Promise.resolve([]),
        ]);

        const schoolYearsById = Object.fromEntries((schoolYears || []).map(y => [y.id, y]));
        const fullSemLabel = (sem) => {
          if (!sem) return null;
          const year = schoolYearsById[sem.schoolYearId];
          return year ? `${year.label} · ${sem.label}` : sem.label;
        };
        const semestersWithLabel = (semesters || []).map(sm => ({ ...sm, fullLabel: fullSemLabel(sm) }));

        const active = semestersWithLabel.find(sm => sm.active) || semestersWithLabel[0] || null;

        // Effective section = section of the most recent ENROLLED enrollment.
        // Enrollments arrive sorted by enrolled_at desc from the backend.
        const enrollmentList = enrollments || [];
        const activeEnrollment = active
          ? enrollmentList.find(e => e.semesterId === active.id && (e.status || "ENROLLED") === "ENROLLED")
          : null;
        const latestEnrollment = enrollmentList[0] || null;
        const effectiveEnrollment = activeEnrollment
          || enrollmentList.find(e => (e.status || "ENROLLED") === "ENROLLED")
          || null;
        const effectiveSectionId = effectiveEnrollment ? effectiveEnrollment.sectionId : null;

        const section = effectiveSectionId ? sections.find(se => se.id === effectiveSectionId) : null;
        const grade = section ? gradeLevels.find(g => g.id === section.gradeLevelId) : null;
        const sectionInfo = section ? {
          section, grade,
          label: grade ? `${grade.name} · ${section.name}` : section.name,
        } : null;

        const isEnrolledForActive = !!activeEnrollment;

        let cls = [], subjects = [], employees = [];
        if (active && effectiveSectionId && isEnrolledForActive) {
          [cls, subjects, employees] = await Promise.all([
            fetch(`${API_BASE}/api/class-offerings?semesterId=${active.id}&sectionId=${effectiveSectionId}`).then(r => r.json()).catch(() => []),
            fetch(`${API_BASE}/api/subjects`).then(r => r.json()).catch(() => []),
            fetch(`${API_BASE}/api/employees?all=true`).then(r => r.json()).catch(() => []),
          ]);
        }

        if (cancelled) return;
        setState({
          loading: false,
          error: "",
          classes: cls,
          activeSemester: active,
          sectionInfo,
          effectiveSectionId,
          isEnrolledForActive,
          latestEnrollment,
          hasAnyEnrollment: enrollmentList.length > 0,
          lookups: {
            subjects:  Object.fromEntries(subjects.map(r => [r.id, r])),
            employees: Object.fromEntries(employees.map(r => [r.id, r])),
            semesters: Object.fromEntries(semestersWithLabel.map(r => [r.id, r])),
            sections:  Object.fromEntries((sections || []).map(r => [r.id, r])),
            gradeLevels: Object.fromEntries((gradeLevels || []).map(r => [r.id, r])),
          },
          enrollments: enrollmentList,
        });
      } catch (e) {
        if (!cancelled) setState(p => ({ ...p, loading: false, error: e.message }));
      }
    })();
    return () => { cancelled = true; };
  }, [studentId]);

  return state;
}

/** Stable hue from any seed — used for subject card accents. */
function hueFor(seed) {
  const str = String(seed || "•");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
}

/** 2-letter abbreviation for the subject icon (code first, then title). */
function abbrev(src) {
  const s = (src || "").toString();
  const words = s.replace(/[^A-Za-z0-9 ]/g, " ").split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return s.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "S";
}

/** Initials from a full name (for teacher chips). */
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export default function StudentDashboard({ user }) {
  const link = user?.linkedEntity;
  const studentId = link?.id;
  const data = useStudentSchedule(studentId);
  const {
    loading, error, classes, lookups,
    activeSemester, sectionInfo, effectiveSectionId,
    isEnrolledForActive, latestEnrollment, hasAnyEnrollment,
  } = data;

  const [profileOpen, setProfileOpen] = useState(false);

  const latestSemesterLabel = latestEnrollment
    ? (lookups.semesters?.[latestEnrollment.semesterId]?.fullLabel
       || lookups.semesters?.[latestEnrollment.semesterId]?.label
       || `#${latestEnrollment.semesterId}`)
    : null;
  const activeSemesterLabel = activeSemester?.fullLabel || activeSemester?.label;

  // Distinct teacher count for the stats strip — gives the student a sense
  // of "how many teachers do I see this term" at a glance.
  const teacherCount = useMemo(() => {
    const ids = new Set(classes.map(c => c.teacherStaffId).filter(Boolean));
    return ids.size;
  }, [classes]);

  const subjectCount = classes.length;

  return (
    <RoleDashboard
      eyebrow="Student"
      title={`Welcome${link?.displayName ? `, ${link.displayName}` : ""}.`}
      sub={link?.studentNumber
        ? `Student #${link.studentNumber}. Your class, subjects, and academic profile in one place.`
        : "Your class, subjects, and academic profile in one place."}
      user={user}
    >
      {/* ── Snapshot card: identity + section + stat tiles ───────── */}
      {!loading && !error && sectionInfo && (
        <section className={m.snapshot} aria-label="Academic snapshot">
          <header className={m.snapHead}>
            <div className={m.snapIdent}>
              {link?.photoUrl
                ? <img src={link.photoUrl} alt="" className={m.snapAvatarImg} />
                : <div className={m.snapAvatar}>{initials(link?.displayName)}</div>}
              <div className={m.snapIdentText}>
                <div className={m.snapEyebrow}>This Term</div>
                <div className={m.snapName}>{link?.displayName || "Student"}</div>
                <div className={m.snapMeta}>
                  <span className={m.snapPillStrong}>{sectionInfo.label}</span>
                  {activeSemesterLabel && (
                    <span className={m.snapPill}>{activeSemesterLabel}</span>
                  )}
                  {link?.studentNumber && (
                    <span className={m.snapPillMono}>#{link.studentNumber}</span>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              className={m.snapProfileBtn}
              onClick={() => setProfileOpen(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2"/>
              </svg>
              View academic profile
            </button>
          </header>

          <div className={m.statRow}>
            <StatChip label="Subjects" value={subjectCount} hint="this term" />
            <StatChip label="Teachers" value={teacherCount} hint="distinct" />
            <StatChip
              label="Adviser"
              value={(() => {
                const adv = sectionInfo.section?.adviserStaffId
                  ? lookups.employees?.[sectionInfo.section.adviserStaffId]?.name
                  : null;
                return adv ? abbrev(adv) : "—";
              })()}
              hint={(() => {
                const adv = sectionInfo.section?.adviserStaffId
                  ? lookups.employees?.[sectionInfo.section.adviserStaffId]?.name
                  : null;
                return adv || "Not yet assigned";
              })()}
            />
            <StatChip
              label="Enrolment"
              value={isEnrolledForActive ? "Active" : "Inactive"}
              hint={isEnrolledForActive ? "for this term" : (latestSemesterLabel || "no enrolment")}
              tone={isEnrolledForActive ? "good" : "warn"}
            />
          </div>
        </section>
      )}

      <div className={s.section}>
        <div className={s.sectionHead}>
          My Schedule {activeSemesterLabel && `· ${activeSemesterLabel}`}
        </div>

        {loading && (
          <div className={m.skel}>
            <div className={m.skelRow} />
            <div className={m.skelRow} />
            <div className={m.skelRow} />
          </div>
        )}
        {!loading && error && <div className={s.error}>{error}</div>}

        {!loading && !error && !hasAnyEnrollment && (
          <div className={s.empty}>
            You're not enrolled in a section yet. Once an admin enrolls you, your class schedule will appear here.
          </div>
        )}

        {!loading && !error && hasAnyEnrollment && classes.length === 0 && (
          <div className={s.diagnosis}>
            <div className={s.diagnosisHead}>Nothing to show yet — here's why</div>
            {sectionInfo && <div>You're in: <b>{sectionInfo.label}</b></div>}
            <div>Active semester: <b>{activeSemesterLabel || "no active semester set"}</b></div>

            {!activeSemester && (
              <div className={s.diagnosisWarn}>
                No semester is currently marked active. Ask an admin to set one in Academic Directory → Semesters.
              </div>
            )}

            {activeSemester && !isEnrolledForActive && (
              <div className={s.diagnosisWarn}>
                You don't have an enrollment for <b>{activeSemesterLabel}</b>.
                {latestSemesterLabel
                  ? <> Your most recent enrollment is <b>{latestSemesterLabel}</b>.</>
                  : <> You have no enrollment records yet.</>}
                <br />
                Ask an admin to enroll you for the active semester.
              </div>
            )}

            {activeSemester && isEnrolledForActive && effectiveSectionId && (
              <div className={s.diagnosisWarn}>
                No subjects have been scheduled for <b>{sectionInfo?.label}</b> in <b>{activeSemesterLabel}</b> yet.
                Ask an admin to add class offerings (subject + teacher + schedule) for this section.
              </div>
            )}
          </div>
        )}

        {/* Premium subject card grid — replaces the plain bullet list. */}
        {classes.length > 0 && (
          <div className={m.subjGrid}>
            {classes.map((c, i) => {
              const subj = lookups.subjects[c.subjectId];
              const teacher = c.teacherStaffId ? lookups.employees[c.teacherStaffId] : null;
              const hue = hueFor(subj?.code || subj?.title || c.subjectId);
              const ab  = abbrev(subj?.code || subj?.title || `S${c.subjectId}`);
              return (
                <article
                  key={c.id}
                  className={m.subjCard}
                  style={{ "--sHue": hue, animationDelay: `${Math.min(i, 8) * 40}ms` }}>
                  <header className={m.subjHead}>
                    <div className={m.subjIcon} aria-hidden="true">{ab}</div>
                    <div className={m.subjIdent}>
                      <div className={m.subjTitle}
                           title={subj?.title || `Subject #${c.subjectId}`}>
                        {subj?.title || `Subject #${c.subjectId}`}
                      </div>
                      {subj?.code && <div className={m.subjCode}>{subj.code}</div>}
                    </div>
                  </header>

                  <div className={m.subjBody}>
                    {teacher?.name && (
                      <div className={m.subjTeacher}>
                        {teacher.photoUrl
                          ? <img src={teacher.photoUrl} alt="" className={m.subjTeacherAvatarImg} />
                          : <div className={m.subjTeacherAvatar}>{initials(teacher.name)}</div>}
                        <div className={m.subjTeacherBody}>
                          <div className={m.subjTeacherLabel}>Teacher</div>
                          <div className={m.subjTeacherName} title={teacher.name}>
                            {teacher.name}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className={m.subjFacts}>
                      {c.schedule && (
                        <div className={m.subjFact}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="2"
                               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="9"/>
                            <polyline points="12 7 12 12 15 14"/>
                          </svg>
                          <span>{c.schedule}</span>
                        </div>
                      )}
                      {c.room && (
                        <div className={m.subjFact}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="2"
                               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z"/>
                            <circle cx="12" cy="10" r="3"/>
                          </svg>
                          <span>Room {c.room}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <MySportsWidget studentId={link?.id} />

      {/* Items the IT teacher requires this student to bring/buy.
          The widget self-hides when the student has nothing on file
          so it doesn't show "no items" noise to the 95% of students
          with no pending requirements. */}
      <LabRequirementsWidget studentId={link?.id} />

      {profileOpen && (
        <AcademicProfileModal
          user={user}
          data={data}
          onClose={() => setProfileOpen(false)}
        />
      )}
    </RoleDashboard>
  );
}

// ── Small pieces ────────────────────────────────────────────────────────────

function StatChip({ label, value, hint, tone }) {
  return (
    <div className={`${m.statChip} ${tone === "good" ? m.statChipGood : ""} ${tone === "warn" ? m.statChipWarn : ""}`}>
      <div className={m.statChipLabel}>{label}</div>
      <div className={m.statChipValue}>{value ?? "—"}</div>
      <div className={m.statChipHint} title={hint}>{hint}</div>
    </div>
  );
}

/** Read-only academic profile modal. Surfaces every piece of information the
 *  student's session and the live enrolment fetch already carry — no new API
 *  surface, no role escalation. Closes on Escape or backdrop click. */
function AcademicProfileModal({ user, data, onClose }) {
  const link = user?.linkedEntity;
  const { sectionInfo, activeSemester, enrollments, lookups, isEnrolledForActive } = data;

  // Escape-to-close, with the listener scoped so it can't leak between mounts.
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const enrollmentRows = (enrollments || []).slice().sort((a, b) => {
    const da = a.enrolledAt || "";
    const db = b.enrolledAt || "";
    return db.localeCompare(da);
  });

  return (
    <div className={m.modalBackdrop}>
      <div
        className={m.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="academicProfileTitle"
        onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className={m.modalClose}
          onClick={onClose}
          aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.4"
               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6"  x2="6"  y2="18"/>
            <line x1="6"  y1="6"  x2="18" y2="18"/>
          </svg>
        </button>

        <header className={m.modalHeader}>
          {link?.photoUrl
            ? <img src={link.photoUrl} alt="" className={m.modalAvatarImg} />
            : <div className={m.modalAvatar}>{initials(link?.displayName)}</div>}
          <div className={m.modalIdent}>
            <div className={m.modalEyebrow}>Academic Profile</div>
            <h2 id="academicProfileTitle" className={m.modalName}>
              {link?.displayName || "Student"}
            </h2>
            <div className={m.modalSub}>
              {link?.studentNumber && <span className={m.snapPillMono}>#{link.studentNumber}</span>}
              {sectionInfo?.label && <span className={m.snapPillStrong}>{sectionInfo.label}</span>}
              {(activeSemester?.fullLabel || activeSemester?.label) && (
                <span className={m.snapPill}>{activeSemester.fullLabel || activeSemester.label}</span>
              )}
              <span className={`${m.snapPill} ${isEnrolledForActive ? m.statChipGood : m.statChipWarn}`}>
                {isEnrolledForActive ? "Active enrolment" : "Not enrolled this term"}
              </span>
            </div>
          </div>
        </header>

        <div className={m.modalBody}>
          <Section title="Identity">
            <KV label="Full name"      value={link?.displayName} />
            <KV label="Student number" value={link?.studentNumber} mono />
            <KV label="Email"          value={user?.email} />
            <KV label="Role"           value={user?.role} />
          </Section>

          <Section title="Current Section">
            <KV label="Section"        value={sectionInfo?.label} />
            <KV label="Grade level"    value={sectionInfo?.grade?.name} />
            <KV label="Active term"    value={activeSemester?.fullLabel || activeSemester?.label} />
            <KV label="Adviser"        value={
              sectionInfo?.section?.adviserStaffId
                ? lookups.employees?.[sectionInfo.section.adviserStaffId]?.name
                : null
            } />
          </Section>

          <Section title="Enrolment history" sub={`${enrollmentRows.length} record${enrollmentRows.length === 1 ? "" : "s"}`}>
            {enrollmentRows.length === 0 ? (
              <div className={m.kvEmpty}>No enrolment records yet.</div>
            ) : (
              <ol className={m.history}>
                {enrollmentRows.map(e => {
                  const sec = lookups.sections?.[e.sectionId];
                  const grade = sec ? lookups.gradeLevels?.[sec.gradeLevelId] : null;
                  const sem = lookups.semesters?.[e.semesterId];
                  const status = (e.status || "ENROLLED").toUpperCase();
                  return (
                    <li key={e.id} className={m.historyRow}>
                      <div className={`${m.historyDot} ${m["histStat_" + status.toLowerCase()] || ""}`} aria-hidden="true" />
                      <div className={m.historyBody}>
                        <div className={m.historyTitle}>
                          {sec
                            ? (grade ? `${grade.name} · ${sec.name}` : sec.name)
                            : `Section #${e.sectionId}`}
                        </div>
                        <div className={m.historyMeta}>
                          {sem?.fullLabel || sem?.label || `Semester #${e.semesterId}`}
                          {e.enrolledAt && <> · {new Date(e.enrolledAt).toLocaleDateString()}</>}
                        </div>
                      </div>
                      <span className={`${m.historyStatus} ${m["histStat_" + status.toLowerCase()] || ""}`}>
                        {status}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </Section>

          <div className={m.modalFootHint}>
            <strong>Need to move to a different section?</strong>{" "}
            Section transfer requests will be available in a future update — until
            then, ask the registrar to update your enrolment.
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, sub, children }) {
  return (
    <section className={m.modalSection}>
      <div className={m.modalSectionHead}>
        <h3 className={m.modalSectionTitle}>{title}</h3>
        {sub && <span className={m.modalSectionSub}>{sub}</span>}
      </div>
      <div className={m.kvGrid}>{children}</div>
    </section>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className={m.kvRow}>
      <div className={m.kvLabel}>{label}</div>
      <div className={`${m.kvValue} ${mono ? m.kvMono : ""}`}>
        {value || <span className={m.kvDash}>—</span>}
      </div>
    </div>
  );
}
